import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Link, useLoadRoute, useSearch } from '@tanstack/react-location';
import { format } from 'date-fns';
import { ChangeEvent, useMemo, useState } from 'react';

import PageStatus from '../../components/PageStatus';
import { useQuotations } from '../../hooks/quotation';
import { RouteLocationGenerics } from '../../routes';
import { numberFormatter } from '../../utils/formatter';

const Quotations = () => {
  const { size } = useSearch<RouteLocationGenerics>();
  const { status, data } = useQuotations(size);
  const loadRoute = useLoadRoute();
  const [filter, setFilter] = useState('');

  const quotations = useMemo(() => {
    if (!data) return [];
    const keyword = filter.trim().toLowerCase();
    if (!keyword) return data;
    return data.filter(
      (quotation) =>
        `${quotation.running_number}`.toLowerCase().includes(keyword) ||
        (quotation.name ?? '').toLowerCase().includes(keyword) ||
        (quotation.staff?.name ?? '').toLowerCase().includes(keyword)
    );
  }, [data, filter]);

  const onFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
  };

  if (status === 'loading') {
    return <PageStatus />;
  } else if (status === 'error') {
    return <PageStatus status="ERROR" />;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <input
          type="text"
          className="input input-bordered input-sm w-full max-w-xs"
          placeholder="ค้นหา เลขที่ / ลูกค้า / ผู้เสนอราคา"
          value={filter}
          onChange={onFilterChange}
        />
        <span className="whitespace-nowrap text-sm">ทั้งหมด {quotations.length} รายการ</span>
      </div>
      <div className="w-full overflow-x-auto rounded-lg shadow-xl">
        <table className="table w-full">
          <thead>
            <tr>
              <th className="bg-slate-200 text-sm">เลขที่</th>
              <th className="bg-slate-200 text-sm">ลูกค้า</th>
              <th className="bg-slate-200 text-sm">ผู้เสนอราคา</th>
              <th className="bg-slate-200 text-sm">วันที่สร้าง</th>
              <th className="bg-slate-200 text-center text-sm">ยอดรวม (บาท)</th>
              <th className="bg-slate-200 text-sm"></th>
            </tr>
          </thead>
          <tbody>
            {quotations.length > 0 ? (
              quotations.map((quotation) => (
                <tr key={quotation.id}>
                  <th>{quotation.running_number}</th>
                  <td>{quotation.name || '-'}</td>
                  <td>{quotation.staff?.name || '-'}</td>
                  <td>
                    <span className="badge w-[100px]">
                      {format(new Date(quotation.create_date), 'dd/MM/yyyy')}
                    </span>
                  </td>
                  <td className="text-right">{numberFormatter(2).format(quotation.total ?? 0)}</td>
                  <td className="text-center">
                    <Link
                      className="btn btn-square btn-xs"
                      to={`/quotations/${quotation.running_number}`}
                      onMouseEnter={() =>
                        loadRoute({ to: `/quotations/${quotation.running_number}` })
                      }
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    </Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center">
                  ไม่พบข้อมูล
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Quotations;
